import type { IlistDownloadItemType } from './type'
import { APP_NAME } from '@/components/PlatformAvatar'
import { EMAIL_TEMPLATE_SOURCE_LIST, MESSAGE_RESOURCE, OPERATE_STATUS, SMS_TEMPLATE_SOURCE_LIST, TRACK_TEMPLATE_SOURCE_LIST } from '@/constant'
import { STATUS } from '@/containers/BillManagement/CodBill/constant'
import { detectSnapshotExpired } from '@/services/apis/batchMergeShip'
import { useDispatch, useSelector } from '@/store'
import { queryS3DownloadUrl, showPickupDetail } from '@/store/notificationSlice'
import type { MessageItemType } from '@/store/notificationSlice/type'
import { windowOpen } from '@/utils/browser'
import { jumpToOneclub } from '@/utils/user'
import { OneMessage } from '@yy/one-ui'
import { cloneDeep, get } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { formatDateFn } from '@/utils/format/utils'
import NP from 'number-precision'

interface MessageInfo {
  /** 站内信标题 */
  title: string
  /**站内信内容第一行 */
  content: string
  /** 站内信标题前缀 */
  suffix?: string
  /** 站内信内容第二行（某些站内信有两行内容） */
  content1?: string
}

interface BtnConfigsType {
  btnText: string
  onClick: (p?: any) => void
  show: string | boolean
}

export type StatusTypes = 'success' | 'normal' | 'fail'

interface Params {
  messageItem: MessageItemType
  onClose?: () => void
}

const translateExtInfo = (value: string, lang: string) => {
  let res = value
  try {
    const object = JSON.parse(value)
    res = object?.[lang] || object?.en
  } catch (e) {}
  return res
}

/**
 * 消息中心信息展示
 */
export function useGetMessageInfo({ messageItem, onClose }: Params) {
  const history = useHistory()

  const { t: originT } = useTranslation()
  const dispatch = useDispatch()
  const lang = useSelector((state) => state.language.languageType)
  const messageConfig = useSelector((state) => state.common.messageConfig)
  const mergeTipsShowThreshold = useSelector((state) => state.configSlice.mergeTipsShowThreshold)
  const sellerName = useSelector((state) => state.user.info.sellerName)

  // 格式化一下
  const item = useMemo(() => {
    const formatItem = cloneDeep(messageItem)
    if (!formatItem?.extInfo) {
      formatItem.extInfo = {}
    }
    if (formatItem?.extInfo?.appId) {
      formatItem.extInfo.appId = APP_NAME[formatItem.extInfo.appId]
    }
    if (formatItem?.extInfo?.submitTime) {
      formatItem.extInfo.submitTime = formatDateFn(messageItem?.createTime)
    }
    if (formatItem?.extInfo?.productName) {
      formatItem.extInfo.productName = translateExtInfo(formatItem.extInfo.productName, lang)
    }
    return formatItem
  }, [messageItem, lang])

  // 统一注入extInfo
  const t = useCallback(
    (key: string, otherInfo?: Record<string, string>) =>
      originT(key, {
        ...item.extInfo,
        ...otherInfo,
      }),
    [item.extInfo, originT]
  )

  /** 是否资质审核 */
  const isKycApproval = item.msgSource === MESSAGE_RESOURCE.SELLER_KYC_APPROVAL_COMPLETE

  /** 消息状态 */
  const messageStatus = useMemo<StatusTypes>(() => {
    if (item.msgSource === MESSAGE_RESOURCE.createUser) {
      return 'normal'
    }

    if (item.extInfo?.status === OPERATE_STATUS.success) {
      return 'success'
    }

    return 'fail'
  }, [item.msgSource, item.extInfo])

  const formatData = useCallback(
    (data) => {
      return {
        title: t(data.title),
        content: Array.isArray(data.content) ? data?.content?.map(t).join('') : t(data.content),
        content1: t(data.content1),
      }
    },
    [t]
  )

  interface Data {
    title: string
    content: string | string[]
    content1?: string
  }

  interface Action {
    text: string
    url?: string
    query?: Array<{ key: string; value?: any; text?: string }>
    windowOpen?: {
      value: string
    }
    show?: Array<{
      key: string
      // boolean/else
      value: any
    }>
  }

  interface getConfigResp {
    type?: string
    data?: Data
    action?: Action | Action[]
  }

  const getConfig = useCallback<(type: string) => getConfigResp>(
    (type) => {
      const config = messageConfig.find((messageItem) => messageItem.type === type)
      if (config) {
        // no conditions
        if (!config.list) {
          return config
        }

        const matchData = config.list.find((matchItem) => {
          if (!matchItem.conditions) {
            return true
          }

          const successMatch = matchItem.conditions?.every((condition) => {
            const value = get(item, condition.key)
            const compareValue = condition.value

            if (typeof compareValue === 'boolean') {
              return !!value === compareValue
            }

            return value === compareValue
          })

          if (successMatch) {
            return true
          }

          return false
        })

        return matchData || config
      }

      return null
    },
    [item, messageConfig]
  )

  // 消息中心通知
  const messageInfo = useMemo<MessageInfo>(() => {
    const config = getConfig(item.msgSource)

    // find the config
    if (config) {
      return formatData(config.data)
    }

    // 充值到账提醒
    if (item.msgSource === MESSAGE_RESOURCE.walletDeposit) {
      return {
        title: t('message.rechargeTitle'),
        content: t('message.rechargeContent'),
        suffix: t(`wallet.account${item.extInfo.sourceCountry}`),
      }
    }

    // 余额到期提醒
    if (item.msgSource === MESSAGE_RESOURCE.walletBalanceExpire) {
      return {
        title: t('message.walletExpiredTitle'),
        content: t('message.walletExpiredContent'),
        suffix: t(`wallet.account${item.extInfo.sourceCountry}`),
      }
    }

    // 呼叫揽收成功
    const pickupDetail = item.extInfo.pickupInfo ? JSON.parse(item.extInfo.pickupInfo) : {}
    const providerCode = pickupDetail?.providerCode
    if (item.msgSource === MESSAGE_RESOURCE.callPickup) {
      if (item.extInfo.status === 'SUCCESS') {
        return {
          title: originT('order.list.pickUp.success', {
            express: providerCode,
          }),
          content: t('order.list.pickUp.successTips'),
        }
      } else {
        // 呼叫失败
        return {
          title: originT('order.list.pickUp.fail', { express: providerCode }),
          content: t('order.list.pickUp.failTips', {
            faildReason: pickupDetail.ticketMessage,
          }),
        }
      }
    }
    // 取消呼叫揽收成功
    if (item.msgSource === MESSAGE_RESOURCE.cancelPickup) {
      if (item.extInfo.status === 'SUCCESS') {
        return {
          title: originT('order.list.pickUp.canceled', {
            express: providerCode,
          }),
          content: t('order.list.pickUp.hadCanceled'),
        }
      } else {
        // 取消失败
        return {
          title: originT('order.list.pickUp.cancelFail', {
            express: providerCode,
          }),
          content: t('order.list.pickUp.cancelFailTips', {
            faildReason: pickupDetail.ticketMessage,
          }),
        }
      }
    }
    // 更新揽收
    if (item.msgSource === MESSAGE_RESOURCE.updatePickup) {
      if (item.extInfo.status === 'SUCCESS') {
        return {
          title: originT('pickup.updateTitle', { express: providerCode }),
          content: t('pickup.updateContent'),
        }
      }
    }

    // 服务申请
    if (isKycApproval) {
      const successOrFail = item.extInfo.sellerKycApprovalStatus === 'CHECK_PASS' ? t('serviceApply.message.success') : t('serviceApply.message.fail')
      return {
        title: originT(item.extInfo.appApplyType === 'express_account' ? 'subaccount.apply.message' : 'serviceApply.message', {
          successOrFail,
        }),
        content: '',
      }
    }

    // 添加优先费失败
    if (item.msgSource === MESSAGE_RESOURCE.ADD_PRIORITY_FEE_FAIL) {
      return {
        title: t('immediate.addPriorityFeeFail'),
        content: item.extInfo.bizErrorMsg || '',
      }
    }

    /** 自定义模板审核 start */
    const emailText = originT('tracking.emailTemplate')
    const smsText = originT('tracking.smsTemplate')
    const trackingPageText = originT('tracking.trackingPageTemplate')

    const BAND_TYPE_MAP = {
      [MESSAGE_RESOURCE.EMAIL_TEMPLATE_BAN]: emailText,
      [MESSAGE_RESOURCE.EMAIL_TEMPLATE_UNBAN]: emailText,
      [MESSAGE_RESOURCE.SMS_TEMPLATE_BAN]: smsText,
      [MESSAGE_RESOURCE.SMS_TEMPLATE_UNBAN]: smsText,
      [MESSAGE_RESOURCE.TRACK_TEMPLATE_BAN]: trackingPageText,
      [MESSAGE_RESOURCE.TRACK_TEMPLATE_UNBAN]: trackingPageText,
    }

    const AUDIT_TYPE_MAP = {
      [MESSAGE_RESOURCE.EMAIL_TEMPLATE_AUDIT_SUCCESS]: emailText,
      [MESSAGE_RESOURCE.EMAIL_TEMPLATE_AUDIT_FAIL]: emailText,
      [MESSAGE_RESOURCE.SMS_TEMPLATE_AUDIT_SUCCESS]: smsText,
      [MESSAGE_RESOURCE.SMS_TEMPLATE_AUDIT_FAIL]: smsText,
      [MESSAGE_RESOURCE.TRACK_TEMPLATE_AUDIT_SUCCESS]: trackingPageText,
      [MESSAGE_RESOURCE.TRACK_TEMPLATE_AUDIT_FAIL]: trackingPageText,
    }

    if ([MESSAGE_RESOURCE.EMAIL_TEMPLATE_AUDIT_SUCCESS, MESSAGE_RESOURCE.SMS_TEMPLATE_AUDIT_SUCCESS, MESSAGE_RESOURCE.TRACK_TEMPLATE_AUDIT_SUCCESS].includes(item.msgSource)) {
      return {
        title: t('tracking.audit.passTitle', {
          type: AUDIT_TYPE_MAP[item.msgSource],
        }),
        content: t('tracking.audit.passContent', {
          type: AUDIT_TYPE_MAP[item.msgSource],
        }),
      }
    }

    if ([MESSAGE_RESOURCE.EMAIL_TEMPLATE_AUDIT_FAIL, MESSAGE_RESOURCE.SMS_TEMPLATE_AUDIT_FAIL, MESSAGE_RESOURCE.TRACK_TEMPLATE_AUDIT_FAIL].includes(item.msgSource)) {
      return {
        title: t('tracking.audit.noPassTitle', {
          type: AUDIT_TYPE_MAP[item.msgSource],
        }),
        content: t('tracking.audit.noPassContent', {
          type: AUDIT_TYPE_MAP[item.msgSource],
        }),
      }
    }

    if ([MESSAGE_RESOURCE.EMAIL_TEMPLATE_BAN, MESSAGE_RESOURCE.SMS_TEMPLATE_BAN, MESSAGE_RESOURCE.TRACK_TEMPLATE_BAN].includes(item.msgSource)) {
      return {
        title: t('tracking.common.templateDisabled', {
          type: BAND_TYPE_MAP[item.msgSource],
        }),
        content: t('tracking.common.templateDisabledReason', {
          type: BAND_TYPE_MAP[item.msgSource],
        }),
      }
    }

    if ([MESSAGE_RESOURCE.EMAIL_TEMPLATE_UNBAN, MESSAGE_RESOURCE.SMS_TEMPLATE_UNBAN, MESSAGE_RESOURCE.TRACK_TEMPLATE_UNBAN].includes(item.msgSource)) {
      return {
        title: t('tracking.common.templateDisabledRemove', {
          type: BAND_TYPE_MAP[item.msgSource],
        }),
        content: t('tracking.common.templateDisabledRemoveText', {
          type: BAND_TYPE_MAP[item.msgSource],
        }),
      }
    }
    /** 自定义模板审核 end */

    // 隐私协议通知
    if (item.msgSource === MESSAGE_RESOURCE.privacy) {
      const salesConfigRiskType = item.extInfo.salesConfigRiskType
      if (item.extInfo.status === 'SUCCESS') {
        return {
          title: t(`notify.privacy.titleSuccess.${salesConfigRiskType}`),
          content: t(`notify.privacy.contentSuccess.${salesConfigRiskType}`),
        }
      }
      if (item.extInfo.status === 'FAIL') {
        return {
          title: t(`notify.privacy.titleFail.${salesConfigRiskType}`),
          content: t(`notify.privacy.contentFail.${salesConfigRiskType}`),
        }
      }
    }
    /** 优惠活动站内信 start */
    // 活动类型
    const actType = t(`activity.${item.extInfo.typeLocaleKey}`)

    // 用户领取优惠成功
    if (item.msgSource === MESSAGE_RESOURCE.OS_GIVEAWAY_SUCCEED) {
      return {
        title: t('message.rechargeTitle'),
        content: t('message.rechargeContent'),
        suffix: t(`wallet.account${item.extInfo.countryCode}`),
      }
    }
    // 用户活动参与失败
    if (item.msgSource === MESSAGE_RESOURCE.OS_GIVEAWAY_FAILED) {
      return {
        title: t('promotion.activity.fail.title'),
        content: t('promotion.activity.fail.tip'),
      }
    }
    // 用户未参与活动提醒
    if (item.msgSource === MESSAGE_RESOURCE.OS_GIVEAWAY_NOT_JOIN) {
      return {
        title: t('promotion.act.overtime.title', { discountRecordType: actType }),
        content: t('promotion.act.overtime.tip', { sellerName, amount: String(NP.divide(item.extInfo.amount, 100)), discountRecordType: actType }),
      }
    }
    // 用户活动参与成功，未消费
    if (item.msgSource === MESSAGE_RESOURCE.OS_GIVEAWAY_NOT_PAY) {
      return {
        title: t('promotion.price.overtime.title', { discountRecordType: actType }),
        content: t('promotion.price.overtime.tip', { sellerName, amount: String(NP.divide(item.extInfo.amount, 100)) }),
      }
    }
    /** 优惠活动站内信 end */

    return {
      title: '',
      content: '',
    }
  }, [getConfig, item.msgSource, item.extInfo, isKycApproval, originT, formatData, t, sellerName])

  // 运单发货状态需要显示按钮 成功才有
  const isShowLogisticsCheck = useMemo(() => {
    return (
      [
        MESSAGE_RESOURCE.batchCreate,
        MESSAGE_RESOURCE.batchCreateDeliver,
        MESSAGE_RESOURCE.batchMergeDeliver,
        MESSAGE_RESOURCE.updateDeliver,
        MESSAGE_RESOURCE.createDeliver,
        MESSAGE_RESOURCE.ORDER_EXPIRE_TO_HOME,
      ].includes(item.msgSource) ||
      (item.msgSource === MESSAGE_RESOURCE.batchMergeShipDelivery && item.extInfo?.submitSuccessNum > 0)
    )
  }, [item.extInfo?.submitSuccessNum, item.msgSource])

  // 订单发货失败
  const isShowShipFailCheck = useMemo(() => {
    return item.msgSource === MESSAGE_RESOURCE.createDeliver && item.extInfo.status !== OPERATE_STATUS.success && item.extInfo.orderNo
  }, [item.extInfo.orderNo, item.extInfo.status, item.msgSource])

  // 批量创建追踪单
  const isShowBatchCreateWaybillCheck = useMemo(() => {
    return item.msgSource === MESSAGE_RESOURCE.batchCreateWaybillChecking
  }, [item.msgSource])

  // 批量设置服务产品失败
  const isShowExpressFaildCheck = item.msgSource === MESSAGE_RESOURCE.orderExpressTypePreferenceFilling && item.extInfo?.errorExpressTypeCount > 0

  // 需要显示按钮
  const isShowCheck = useMemo(() => {
    return isShowLogisticsCheck || isShowShipFailCheck || isShowBatchCreateWaybillCheck
  }, [isShowBatchCreateWaybillCheck, isShowLogisticsCheck, isShowShipFailCheck])

  // 下载按钮 失败才有
  const isShowDownlaod = useMemo(() => {
    const status = [
      MESSAGE_RESOURCE.batchUpdateDeliver,
      MESSAGE_RESOURCE.batchCreateDeliver,
      MESSAGE_RESOURCE.batchMergeShipDelivery || MESSAGE_RESOURCE.batchCreate,
      MESSAGE_RESOURCE.batchCreateWaybillChecking,
      MESSAGE_RESOURCE.batchMergeDeliver,
    ]
    const isFail = item.extInfo.status === OPERATE_STATUS.fail
    return status.includes(item.msgSource) && item.extInfo?.fileUrl && isFail
  }, [item.extInfo, item.msgSource])

  // 支付按钮
  const isShowPay = useMemo(() => {
    const status = [MESSAGE_RESOURCE.subBillPending, MESSAGE_RESOURCE.subRecurringFail]
    return status.includes(item.msgSource)
  }, [item.msgSource])

  // 充值按钮
  const isShowRecharge = useMemo(() => {
    const status = [
      MESSAGE_RESOURCE.subRecurringNotify,
      MESSAGE_RESOURCE.subTrackThreshold,
      MESSAGE_RESOURCE.subSmsThreshold,
      MESSAGE_RESOURCE.subExpiredNotify,
      MESSAGE_RESOURCE.SUBSCRIBE_SAAS,
      MESSAGE_RESOURCE.SUBSCRIBE_FREIGHT_COUNT,
    ]
    return status.includes(item.msgSource)
  }, [item.msgSource])

  // cod账单，查看账单
  const isShowCheckCodBill = useMemo(() => item.msgSource === MESSAGE_RESOURCE.codBillCheckOut, [item.msgSource])

  // 呼叫揽收 查看按钮
  const isShowPickup = [MESSAGE_RESOURCE.callPickup, MESSAGE_RESOURCE.cancelPickup, MESSAGE_RESOURCE.updatePickup].includes(item.msgSource) && item.extInfo.status === 'SUCCESS'

  const handleDownload = useCallback(() => {
    item.extInfo?.fileUrl && windowOpen(item.extInfo?.fileUrl)
  }, [item])

  const handleCheck = useCallback(() => {
    // 批量设置服务产品失败
    if (isShowExpressFaildCheck) {
      handleDownload()
      return
    }
    // 跳转运单追踪列表
    if (isShowBatchCreateWaybillCheck) {
      history.push('/track/waybillTracking')
      return
    }
    // 跳转订单列表
    if (isShowShipFailCheck) {
      const link =
        item.extInfo?.logisticsType === 'ondemand'
          ? `/immediate/order?t=queryWord&q=${encodeURIComponent(item.extInfo.orderNo)}`
          : `/ship/order/list?t=queryWord&q=${encodeURIComponent(item.extInfo.orderNo)}`
      history.push(`/redirect?url=${encodeURIComponent(link)}`)
      return
    }

    let search_text = ''
    const checkTypes = [MESSAGE_RESOURCE.updateDeliver, MESSAGE_RESOURCE.createDeliver, MESSAGE_RESOURCE.orderStationTurnOff, MESSAGE_RESOURCE.ORDER_EXPIRE_TO_HOME]
    if (checkTypes.includes(item.msgSource)) {
      search_text = item.extInfo?.logisticsNo || ''
    } else {
      search_text = item.extInfo?.bulkNo || ''
    }

    history.push(item.extInfo?.logisticsType === 'ondemand' ? `/immediate/logistics?q=${search_text}` : `/ship/logistics/list?q=${search_text}`)
  }, [
    handleDownload,
    history,
    isShowBatchCreateWaybillCheck,
    isShowExpressFaildCheck,
    isShowShipFailCheck,
    item.extInfo?.bulkNo,
    item.extInfo?.logisticsNo,
    item.extInfo.orderNo,
    item.msgSource,
    item.extInfo?.logisticsType,
  ])

  const handlePay = useCallback(() => {
    history.push('/account/subscribe/bills')
  }, [history])

  const handleRecharge = useCallback(() => {
    history.push('/account/subscribe')
  }, [history])

  const handleCheckCodBill = useCallback(() => {
    history.push(`/billManagement/codBill`, { status: STATUS.HAD_CHECK_OUT })
  }, [history])

  /** 呼叫揽收查看详情 */
  const handleCheckPickup = useCallback(() => {
    dispatch(showPickupDetail(JSON.parse(item.extInfo?.pickupInfo).id, item))
  }, [dispatch, item])

  /** 操作按钮 */
  const btnConfigs = useMemo<BtnConfigsType[]>(() => {
    const config = getConfig(item.msgSource)
    const actions = Array.isArray(config?.action) ? config?.action : config?.action ? [config?.action] : []
    const extraBtnConfigs = actions.map((action) => {
      return {
        btnText: t(action?.text),
        show:
          !!action &&
          (!action?.show ||
            action?.show?.every((showItem) => {
              const value = get(item, showItem.key)
              if (typeof showItem.value === 'boolean') {
                return !!value === showItem.value
              }
              return showItem.value === value
            })),
        onClick: () => {
          const { windowOpen: openAction, url, query = [] } = action || {}
          if (typeof openAction === 'object') {
            const url = get(item, openAction.value)
            windowOpen(url)
          } else if (typeof url === 'string') {
            const queryStr = query
              .map((queryItem) => {
                const { key, value, text } = queryItem
                return [key, typeof value === 'string' ? get(item, value) : text || ''].join('=')
              })
              .join('&')

            const finalStr = `${url}${queryStr ? `?${queryStr}` : ''}`
            history.push(finalStr)
          }
        },
      }
    })

    return [
      ...extraBtnConfigs,
      {
        btnText: t('message.check'),
        onClick: handleCheck,
        show: isShowCheck,
      },
      {
        btnText: t('message.download'),
        onClick: handleDownload,
        show: isShowDownlaod,
      },
      {
        btnText: t('message.subscribe.pay'),
        onClick: handlePay,
        show: isShowPay,
      },
      {
        btnText: t('message.subscribe.recharge'),
        onClick: handleRecharge,
        show: isShowRecharge,
      },
      {
        btnText: t('subscribe.config.billsBtn'),
        onClick: handleCheckCodBill,
        show: isShowCheckCodBill,
      },
      {
        btnText: t('batch.merge.button.text'),
        onClick: async () => {
          const taskId = item?.extInfo?.bulkNo

          const { data: taskRes } = await detectSnapshotExpired({
            taskId,
          })

          const needToPending = mergeTipsShowThreshold ? item.extInfo.succeedNum >= mergeTipsShowThreshold : false

          history.replace(`/ship/order/new-batch-merge?taskId=${taskId}&taskSnapshotVersion=${taskRes?.taskSnapshotVersion}&needToPending=${Number(needToPending)}`)
        },
        show: Number(item.extInfo.expireDay) >= Date.now() && item.extInfo.succeedNum > 0,
      },
      // 合单发货失败
      {
        btnText: t('batch.merge.message.failButton'),
        onClick: async () => {
          await detectSnapshotExpired({
            taskId: item?.extInfo?.bulkNo,
          })
          history.push(`/ship/order/list?t=queryWord&q=${item.extInfo.bulkNo}`)
        },
        show: Number(item.extInfo.expireDay) >= Date.now() && item.extInfo.failedNum > 0,
      },
      // 自定义模板审核状态 查看详情
      {
        btnText: t('subscribe.package.pendingBillModal.showDetail'),
        onClick: () => {
          let url = ''
          if (EMAIL_TEMPLATE_SOURCE_LIST.includes(item.msgSource)) {
            url = '/track/setting/email'
          }
          if (SMS_TEMPLATE_SOURCE_LIST.includes(item.msgSource)) {
            url = '/track/setting/sms'
          }
          if (TRACK_TEMPLATE_SOURCE_LIST.includes(item.msgSource)) {
            url = '/track/setting/trackingPage'
          }
          // 如果当前正处于模板配置，点击查看详情直接刷新页面
          if (url === history.location.pathname) {
            location.href = url
          } else {
            history.push(url)
          }
        },
        show: [...EMAIL_TEMPLATE_SOURCE_LIST, ...SMS_TEMPLATE_SOURCE_LIST, ...TRACK_TEMPLATE_SOURCE_LIST].includes(item.msgSource),
      },
      {
        btnText: t('subscribe.package.pendingBillModal.showDetail'),
        show: isShowPickup,
        onClick: handleCheckPickup,
      },
      {
        btnText: t('subscribe.package.pendingBillModal.showDetail'),
        show: isKycApproval,
        onClick: () => {
          jumpToOneclub('serviceApply')
        },
      },
      ,
    ]
      .filter((item) => item.show)
      .map((item) => ({
        ...item,
        onClick: () => {
          item.onClick()
          onClose && onClose()
        },
      }))
  }, [
    getConfig,
    item,
    t,
    handleCheck,
    isShowCheck,
    handleDownload,
    isShowDownlaod,
    handlePay,
    isShowPay,
    handleRecharge,
    isShowRecharge,
    handleCheckCodBill,
    isShowCheckCodBill,
    isShowPickup,
    handleCheckPickup,
    isKycApproval,
    history,
    mergeTipsShowThreshold,
    onClose,
  ])

  return {
    messageInfo,
    messageStatus,
    btnConfigs,
  }
}

/**
 * 下载中心消息展示
 */
export function useGetDownLoadInfo({ item }: IlistDownloadItemType) {
  const { t: originT } = useTranslation()
  const downloadConfig = useSelector((state) => state.common.downloadConfig)

  // 统一注入item信息
  const t = useCallback((key: string) => originT(key, item), [item, originT])

  /** 消息状态 */
  const messageStatus = useMemo<StatusTypes>(() => {
    if ([1, 4].includes(item.fileStatus)) {
      return 'normal'
    }

    if (item.fileStatus === 2) {
      return 'success'
    }

    if (item.fileStatus === 3) {
      return 'fail'
    }
  }, [item.fileStatus])

  const messageInfo = useMemo<MessageInfo>(() => {
    if (item.fileStatus === 1) {
      return {
        title: t('download.loading'),
        content: t('download.loading'),
      }
    }

    if (item.fileStatus === 2) {
      const current = downloadConfig?.[item.taskType]
      if (current) {
        return {
          title: t(current.title),
          content: t(current.content),
        }
      }

      // 运单导出成功
      return {
        title: t('download.sucContent'),
        content: t('download.logisticSucInvalid'),
      }
    }

    if (item.fileStatus === 3) {
      return {
        title: t('download.failTitle'),
        content: t('download.failContent'),
      }
    }

    if (item.fileStatus === 4) {
      return {
        title: t('download.sucTitle'),
        content: t('download.sucContent'),
      }
    }

    return {
      title: '',
      content: '',
    }
  }, [downloadConfig, item.fileStatus, item.taskType, t])

  const handleDownload = useCallback(
    async (e?: React.MouseEvent) => {
      e && e.stopPropagation()

      const {
        data: { presignedExportFiles },
      } = await (async () => {
        if (item.taskType === 22) {
          try {
            return await queryS3DownloadUrl(item.taskId, false)
          } catch (error) {
            if (error.bizCode === 'LST-134443-B1116') {
              OneMessage.error(t('shipmentProof.export.complete.tips'))
            }

            return await Promise.reject(error)
          }
        }

        return queryS3DownloadUrl(item.taskId)
      })()

      const url = presignedExportFiles[0]?.presignedUrl
      if (url) {
        windowOpen(url)
      }
    },
    [item?.taskId, item?.taskType, t]
  )

  /** 操作按钮 */
  const btnConfigs = useMemo<BtnConfigsType[]>(() => {
    return [
      {
        btnText: t('download.button'),
        onClick: handleDownload,
        show: item.fileUrl,
      },
    ].filter((item) => item.show)
  }, [handleDownload, item.fileUrl, t])

  return {
    messageInfo,
    messageStatus,
    btnConfigs,
  }
}
